/**
 * **Whether a wait has lasted long enough to be worth saying so** — R5 step 7 (M7).
 *
 * A read that resolves in a couple of hundred milliseconds and a spinner drawn for the whole of it
 * are two different experiences of the same wait: the flash is what a reader notices, not the wait.
 * This hook holds the indicator back for {@link INDICATOR_DELAY_MS} and lets it through only if the
 * read is still running by then.
 *
 * **It is a display delay and not a timeout.** Nothing refuses earlier or later because of it, no
 * request is deferred, and every screen using it renders exactly the same states in exactly the
 * same order — one of them simply starts a fraction of a second later. That is what keeps it out of
 * `measurements.md` §5 rule 1's *"no constant may be moved against an inference"*, and it is also
 * why it may never be used to hide a *failure*: a failed read must render its error immediately.
 *
 * **Where it may be used, and where it may not.** M7 is not a blanket policy. Apply it where a
 * spinner exists **and cannot be removed** — never in place of a cache-first paint where one is
 * possible (R5 step 4 deletes Gate Detail's spinner rather than delaying it, because a delayed
 * spinner on a screen that could have painted instantly is still a blank screen), and never on a
 * screen that has no spinner at all. `R5-responsivity.md` §5.3 carries the table.
 */
import { useEffect, useState } from 'react'

/**
 * 🔸 **An author assumption with no distribution behind it**, and the only new constant R5
 * introduces. It is a user-visible number nobody has read: `measurements.md` §3.4 L9 records it as
 * owed. It refuses nothing, so it is not evidence anything is calibrated to.
 */
export const INDICATOR_DELAY_MS = 200

/**
 * `true` once `active` has been continuously true for {@link INDICATOR_DELAY_MS}.
 *
 * Goes back to `false` **immediately** when `active` does — the delay is on appearing, never on
 * disappearing, or a read that resolved would leave its own spinner up for another fifth of a
 * second.
 */
export function useDelayedIndicator(active: boolean, delayMs: number = INDICATOR_DELAY_MS): boolean {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!active) {
      setShown(false)
      return
    }
    const timer = window.setTimeout(() => setShown(true), delayMs)
    return () => window.clearTimeout(timer)
  }, [active, delayMs])
  return shown
}
